import { ActionDatePicker } from '@library/components/datePicker/ActionDatePicker';
import { DateDisplay } from '@library/components/datePicker/DateDisplay';
import { DatePickerButton } from '@library/components/buttons/DatePickerButton';
import { DatePickerCustomInput } from '@library/components/datePicker/DatePickerCustomInput';
import { DateTime } from 'luxon';
import type { Status } from '@library/types/projection';
import { StatusIndicator } from '@library/components/StatusIndicator';
import { classNames } from '@library/utils/twClasses';
import { formatWithTimezoneChange } from '@library/utils/dateTime';
import { useState } from 'react';
import whiteArrowDownIcon from '@library/assets/images/icons/white-arrow-down.svg';

export interface TradingTeamHeaderDatePickerProps {
  dateTimeString?: string;
  onChange: (date: DateTime) => void;
  disabled?: boolean;
  label: string;
  status?: Status | undefined;
  maxDate?: DateTime;
  moreThanOneStartTimeValidation?: boolean;
  uniqueStartTimes?: Array<string>;
  format?: string;
}

export const TradingTeamHeaderDatePicker = ({
  dateTimeString = undefined,
  onChange,
  disabled = false,
  label,
  status = undefined,
  maxDate = undefined,
  moreThanOneStartTimeValidation = false,
  uniqueStartTimes = [],
  format = 'LL/dd/yyyy • t ZZZZ'
}: TradingTeamHeaderDatePickerProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const time = dateTimeString && DateTime.fromISO(dateTimeString);

  if (!time) return null;

  return (
    <div className="flex flex-col">
      <span className="text-soClean-180 whitespace-nowrap text-xs">
        {label}
      </span>
      <ActionDatePicker
        changeTime={onChange}
        customInput={
          <DatePickerCustomInput
            className={classNames(
              isOpen ? 'bg-soFresh-120' : 'bg-soFresh-110',
              'hover:bg-soFresh-120 border-soFresh-140 text-soClean-140 grid h-8 w-full rounded-md border text-xs',
              moreThanOneStartTimeValidation
                ? 'border-roses'
                : 'border-soFresh-140'
            )}
          >
            <DatePickerButton
              className="disabled:bg-soFresh-120 h-8 disabled:cursor-not-allowed"
              disabled={disabled}
            >
              <DateDisplay className="flex items-center gap-1 whitespace-nowrap px-2">
                {status && (
                  <div className="flex w-4 justify-center">
                    <StatusIndicator
                      boardTime={dateTimeString}
                      status={status}
                    />
                  </div>
                )}
                <span className="font-heebo leading-5">
                  {formatWithTimezoneChange(time, format)}
                </span>
                <span className="relative inset-y-0 right-0 flex size-3">
                  <img
                    alt="Chevron Down icon"
                    className={classNames(isOpen ? 'rotate-180' : '')}
                    src={whiteArrowDownIcon}
                  />
                </span>
              </DateDisplay>
            </DatePickerButton>
          </DatePickerCustomInput>
        }
        initialTime={time}
        maxDate={maxDate}
        setTimeOpen={setIsOpen}
        timeOpen={isOpen}
        uniqueStartTimes={uniqueStartTimes}
      />
    </div>
  );
};
